import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {getParticipation, submitResponses} from '../../api/user.api';
import {toast} from 'react-toastify';
import logo from '../../assets/LOGO (2).png';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '../../components/ui';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

function SurveyForm({ survey, onSubmit, loading }) {
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [error, setError] = useState('');

  const questions = survey?.questions || [];
  const current = questions[index];
  const progress = questions.length ? Math.round(((index + 1) / questions.length) * 100) : 0;

  const progressLabel = useMemo(() => {
    if (!questions.length) return '0%';
    return `${progress}%`;
  }, [progress, questions.length]);

  const estimatedMinutes = useMemo(() => {
    if (!questions.length) return 1;
    return Math.max(1, Math.ceil(questions.length / 3));
  }, [questions.length]);

  const handleChange = (q, value) => {
    setAnswers((a) => ({ ...a, [q.id]: value }));
    if (error) setError('');
  };

  const getValue = (q) => answers[q?.id];

  const requireFilled = () => {
    if (!current?.is_required) return true;
    const v = answers[current.id];
    if (current.question_type === 'TEXT') return typeof v === 'string' && v.trim().length > 0;
    return v !== undefined && v !== null && String(v).length > 0;
  };

  const next = () => {
    if (!requireFilled()) {
      setError('Cette question est requise');
      return;
    }
    setIndex((i) => (i < questions.length - 1 ? i + 1 : i));
  };

  const prev = () => {
    if (index > 0) setIndex(index - 1);
  };

  const submitAll = (e) => {
    e.preventDefault();
    if (index < questions.length - 1) {
      next();
      return;
    }
    if (!requireFilled()) {
      setError('Cette question est requise');
      return;
    }
    const payload = questions.map((q) => {
      const value = answers[q.id];
      if (q.question_type === 'TEXT') return { question: q.id, answer_text: value || '' };
      if (typeof value === 'number' || typeof value === 'string') return { question: q.id, choice_id: Number(value) };
      return { question: q.id, answer_text: String(value || '') };
    });
    onSubmit(payload);
  };

  const renderQuestion = () => {
    if (!current) return null;

    switch (current.question_type) {
      case 'TEXT':
        return (
          <div className="space-y-3">
            <textarea
              value={getValue(current) || ''}
              onChange={(e) => handleChange(current, e.target.value)}
              className="min-h-[140px] w-full resize-y rounded-lg border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/20"
              placeholder="Saisir votre réponse..."
            />
          </div>
        );

      case 'SCALE':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center gap-2">
              {[1, 2, 3, 4, 5].map((num) => {
                const selected = getValue(current) === num;
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => handleChange(current, num)}
                    className={`flex-1 h-12 rounded-lg border-2 font-semibold transition duration-200 ${
                      selected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground hover:border-primary/40'
                    }`}
                  >
                    {num}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground px-1">
              <span>Pas du tout</span>
              <span>Tout à fait</span>
            </div>
          </div>
        );

      case 'SINGLE_CHOICE':
      case 'YES_NO':
      case 'MULTIPLE_CHOICE':
        return (
          <div className="space-y-3">
            {current.choices?.map((c) => {
              const selected = String(getValue(current) ?? '') === String(c.id);
              return (
                <label
                  key={c.id}
                  className={`group flex cursor-pointer items-start gap-3 rounded-lg border-2 bg-card p-4 shadow-sm transition duration-200 hover:shadow-md ${
                    selected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <input
                    type="radio"
                    className="mt-1 h-4 w-4 accent-primary"
                    name={`q_${current.id}`}
                    value={c.id}
                    checked={selected}
                    onChange={() => handleChange(current, c.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">{c.text}</div>
                  </div>
                </label>
              );
            })}
          </div>
        );

      default:
        return (
          <div className="rounded-lg border border-border bg-muted/50 p-4 text-sm text-muted-foreground">
            Type de question non supporté: {current.question_type}
          </div>
        );
    }
  };

  return (
    <Card className="shadow-card border-0">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <CardTitle className="font-display text-xl">{survey.title}</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="text-xs">
                Question {index + 1} / {questions.length}
              </Badge>
              <Badge variant="outline" className="text-xs">
                ~{estimatedMinutes} min
              </Badge>
              <Badge variant="outline" className="text-xs">
                Réponses sécurisées
              </Badge>
            </div>
          </div>
          <Badge className="text-xs">{progressLabel}</Badge>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
            <span>Progression</span>
            <span>{progressLabel}</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {current && (
          <form onSubmit={submitAll} className="space-y-6">
            <Card className="shadow-sm border-0 bg-muted/30">
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-xs font-semibold text-primary-foreground flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground">{current.text}</h3>
                    {current.is_required && (
                      <p className="text-xs text-destructive mt-1">* Requis</p>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  {renderQuestion()}
                </div>

                {error && (
                  <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                {index > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prev}
                    disabled={loading}
                  >
                    Précédent
                  </Button>
                )}
              </div>

              {index < questions.length - 1 ? (
                <Button
                  type="button"
                  onClick={next}
                  disabled={loading}
                >
                  Suivant
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={loading || !requireFilled()}
                >
                  {loading ? 'Envoi...' : 'Envoyer mes réponses'}
                </Button>
              )}
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

export default function ParticipationPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [blocked, setBlocked] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await getParticipation(token);
        setData(res);
        const already = res?.participant?.status === 'responded';
        
        if (!res?.survey?.is_active) {
          setBlocked('Ce sondage n\'est pas actif');
        } else if (already) {
          setBlocked('Vous avez déjà répondu à ce sondage');
        }
      } catch (e) {
        toast.error("Lien invalide ou expiré");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleResponsesSubmit = async (answers) => {
    try {
      setSaving(true);
      await submitResponses(token, answers);
      setCompleted(true);
      toast.success('Merci pour votre participation !');
    } catch (e) {
      toast.error('Erreur lors de la soumission des réponses');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background px-4 py-10">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div className="flex items-center justify-center">
            <img src={logo} alt="Easy Survey" className="h-12 w-auto" />
          </div>
          <Card className="shadow-card border-0">
            <CardContent className="p-8 text-center space-y-3">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              <p className="text-sm font-medium text-foreground">Chargement…</p>
              <p className="text-xs text-muted-foreground">Préparation du sondage</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background px-4 py-10">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div className="flex items-center justify-center">
            <img src={logo} alt="Easy Survey" className="h-12 w-auto" />
          </div>
          <Card className="shadow-card border-0">
            <CardContent className="p-8 text-center space-y-2">
              <p className="text-sm font-medium text-foreground">Aucune donnée</p>
              <p className="text-xs text-muted-foreground">Le lien est peut-être invalide ou expiré.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:py-10">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <img src={logo} alt="Easy Survey" className="h-12 w-auto" />
          <p className="text-sm text-muted-foreground">Invitation à répondre à un sondage</p>
        </div>

        {blocked && (
          <Card className="shadow-card border-0 border-l-4 border-l-destructive bg-destructive/5">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Accès indisponible</p>
                  <p className="text-sm text-muted-foreground mt-1">{blocked}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!blocked && !completed && (
          <SurveyForm survey={data.survey} onSubmit={handleResponsesSubmit} loading={saving} />
        )}

        {!blocked && completed && (
          <Card className="shadow-card border-0">
            <CardContent className="p-8 text-center space-y-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-success/10 text-success mx-auto">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Merci pour votre participation</p>
                <p className="text-sm text-muted-foreground mt-1">Vos réponses ont été enregistrées.</p>
              </div>
              <p className="text-xs text-muted-foreground">Vous pouvez fermer cette page.</p>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Easy Survey — Merci de répondre honnêtement. Vos réponses peuvent être traitées de manière confidentielle selon le paramétrage du sondage.
        </p>
      </div>
    </div>
  );
}
